import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle, Zap, Moon, Shield, Trophy, ArrowRight, Mail, Download } from "lucide-react";

const MensMaintenanceGuide = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-700 to-green-600 text-white py-12">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full mb-4">
            <Mail className="w-4 h-4" />
            <span className="text-sm font-medium">Guide Sent to Your Email</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Your Vitality Maintenance Guide
          </h1>
          <p className="text-green-100 text-lg">
            Your foundation is strong. Here's how to keep it that way for years to come.
          </p>
        </div>
      </div>

      {/* Guide Content Preview */}
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Trophy className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Maintenance Guide</h2>
              <p className="text-gray-500 text-sm">For Men With Strong Vitality Foundations</p>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
            <p className="text-green-800 font-medium">
              🎉 Congratulations! Your assessment shows your vitality systems are functioning well. 
              This guide will help you maintain this foundation as you age.
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Maintenance Strategies:</h3>
            
            <div className="space-y-6">
              {/* Section 1 */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-3">
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
                  Protect Your Sleep Quality
                </h4>
                <p className="text-gray-600 mb-3">
                  Sleep is when your body repairs, balances hormones, and consolidates recovery. Protecting sleep quality is the #1 maintenance strategy.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Consistent sleep/wake times (even weekends)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Cool bedroom temperature (65-68°F / 18-20°C)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Magnesium before bed for deeper sleep</span>
                  </li>
                </ul>
              </div>

              {/* Section 2 */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-3">
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">2</span>
                  Maintain Circulation Health
                </h4>
                <p className="text-gray-600 mb-3">
                  Good circulation supports every system in your body. Keep it optimized with:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Regular movement (avoid sitting for 2+ hours)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Nitric oxide boosting foods (beets, leafy greens)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Contrast showers (warm to cold)</span>
                  </li>
                </ul>
              </div>

              {/* Section 3 */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-3">
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">3</span>
                  Stress Management Practices
                </h4>
                <p className="text-gray-600 mb-3">
                  Even with good scores, stress accumulates. Build these buffers:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>5-minute breathwork daily (box breathing)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Weekly nature exposure (minimum 2 hours)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Adaptogenic herbs as daily support</span>
                  </li>
                </ul>
              </div>

              {/* Section 4 */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-3">
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">4</span>
                  Supportive Herbs for Maintenance
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-gray-600">
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="font-semibold text-gray-900">Rhodiola Rosea</p>
                    <p className="text-sm">Maintains energy and mental clarity</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="font-semibold text-gray-900">Cordyceps</p>
                    <p className="text-sm">Supports oxygen utilization and stamina</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="font-semibold text-gray-900">Reishi</p>
                    <p className="text-sm">Immune support and stress balance</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="font-semibold text-gray-900">Schisandra</p>
                    <p className="text-sm">Overall vitality and liver support</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">Explore Our Herb Encyclopedia</h3>
          <p className="text-green-100 mb-6 max-w-lg mx-auto">
            Learn more about the 100+ herbs that can support your continued vitality journey.
          </p>
          <Link to="/herbs">
            <Button className="bg-white text-green-700 hover:bg-green-50 px-8 py-3 text-lg font-semibold rounded-full">
              Browse Herbs <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>

        {/* Back Links */}
        <div className="text-center mt-8 space-x-4">
          <Link to="/mens-scorecard" className="text-green-600 hover:text-green-700 font-medium">
            ← Retake Assessment
          </Link>
          <Link to="/" className="text-gray-500 hover:text-gray-700 font-medium">
            Return Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MensMaintenanceGuide;
